// src/api/estadosReserva.ts
import { base } from "./base";

export type EstadoCategoria = "OK" | "CANCEL" | "PENDIENTE" | "BLOQUEO" | "INFO";
export type ActorCancelacion = "ALUMNO" | "PROFESOR" | "SISTEMA" | "OTRO";

export interface EstadoReserva {
  code: string;
  descripcion: string;
  bloqueante: boolean;
  cuentaHoras: boolean;
  requiereComentario: boolean;
  visibleCliente: boolean;
  color?: string | null;
  orden: number;
  categoria: EstadoCategoria;
  actorCancelacion?: ActorCancelacion | null;
  systemFlag: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateEstadoReservaDto {
  code: string;
  descripcion: string;
  bloqueante?: boolean | 0 | 1;
  cuentaHoras?: boolean | 0 | 1;
  requiereComentario?: boolean | 0 | 1;
  visibleCliente?: boolean | 0 | 1;
  color?: string | null;
  orden?: number;
  categoria?: EstadoCategoria;
  actorCancelacion?: ActorCancelacion | null;
  systemFlag?: boolean | 0 | 1;
}

export type UpdateEstadoReservaDto = Partial<CreateEstadoReservaDto> & {
  code?: string;
};

/* ==================== Helpers ==================== */
function normalize(row: any): EstadoReserva {
  return {
    ...row,
    bloqueante: !!row.bloqueante,
    cuentaHoras: !!row.cuentaHoras,
    requiereComentario: !!row.requiereComentario,
    visibleCliente: !!row.visibleCliente,
    systemFlag: !!row.systemFlag,
  };
}

/* ========= NUEVO: mapeo FrontDTO -> BackendDTO validado ========= */

type BackendCategory = "BOOKING" | "CANCEL" | "BLOCK" | "SYSTEM" | "OTHER";

type BackendCreateDto = {
  code: string;
  label: string; // ← lo que valida el backend
  category: BackendCategory; // ← lo que valida el backend
  requiresComment?: boolean;
  isBlocking?: boolean;
  isConfirmed?: boolean; // no lo usamos, pero el DTO lo permite
  active?: boolean; // idem
  sort?: number;
  color?: string | null;
};

function mapCategoriaToBackend(c?: EstadoCategoria): BackendCategory {
  switch ((c ?? "OK").toUpperCase()) {
    case "OK":
      return "BOOKING";
    case "CANCEL":
      return "CANCEL";
    case "BLOQUEO":
      return "BLOCK";
    case "INFO":
      return "SYSTEM";
    case "PENDIENTE":
      return "OTHER";
    default:
      return "OTHER";
  }
}

function toBackendDto(dto: CreateEstadoReservaDto): BackendCreateDto {
  return {
    code: dto.code,
    label: dto.descripcion, // descripcion -> label
    category: mapCategoriaToBackend(dto.categoria), // categoria -> category (mapeada)
    requiresComment: dto.requiereComentario != null ? !!dto.requiereComentario : undefined,
    isBlocking: dto.bloqueante != null ? !!dto.bloqueante : undefined,
    sort: dto.orden,
    color: dto.color ?? undefined,
    // OJO: active / isConfirmed no los estamos usando
  };
}

function toBackendUpdateDto(
  dto: UpdateEstadoReservaDto
): Partial<BackendCreateDto> & { code?: string } {
  const out: Partial<BackendCreateDto> & { code?: string } = {};

  if (dto.code != null) out.code = dto.code;
  if (dto.descripcion != null) out.label = dto.descripcion;
  if (dto.categoria != null) out.category = mapCategoriaToBackend(dto.categoria);
  if (dto.requiereComentario != null) out.requiresComment = !!dto.requiereComentario;
  if (dto.bloqueante != null) out.isBlocking = !!dto.bloqueante;
  if (dto.orden != null) out.sort = dto.orden;
  if (dto.color !== undefined) out.color = dto.color;

  // Nota: cuentaHoras, visibleCliente, actorCancelacion, systemFlag
  // NO están en el DTO validado del backend y serán ignorados por el whitelist.
  return out;
}

/* ==================== CRUD ==================== */
export async function listEstados(): Promise<EstadoReserva[]> {
  const { data } = await base.get<any[]>("/estados-reserva");
  return data.map(normalize);
}

export async function getEstado(code: string): Promise<EstadoReserva | null> {
  try {
    const { data } = await base.get<any>(`/estados-reserva/${encodeURIComponent(code)}`);
    return normalize(data);
  } catch (err: any) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

export async function createEstado(dto: CreateEstadoReservaDto): Promise<EstadoReserva> {
  const payload = toBackendDto(dto);
  const { data } = await base.post<any>("/estados-reserva", payload);
  return normalize(data);
}

export async function updateEstado(
  oldCode: string,
  dto: UpdateEstadoReservaDto
): Promise<EstadoReserva> {
  const payload = toBackendUpdateDto(dto);
  const { data } = await base.patch<any>(
    `/estados-reserva/${encodeURIComponent(oldCode)}`,
    payload
  );
  return normalize(data);
}

export async function deleteEstado(code: string): Promise<void> {
  await base.delete(`/estados-reserva/${encodeURIComponent(code)}`);
}
